import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Sparkles, Wand2, RefreshCw, Check } from "lucide-react";

const COVER_COST = 8;

export default function AiCoverDialog({ open, onOpenChange, project, onApplied }) {
  const [brief, setBrief] = useState("");
  const [style, setStyle] = useState("");
  const [preview, setPreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [applying, setApplying] = useState(false);

  const reset = () => { setPreview(null); setBrief(""); setStyle(""); };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/cover/generate`, { brief: brief.trim(), style: style.trim() });
      setPreview(data.image);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error === "out_of_credits") toast.error(`Not enough credits — a cover costs ${COVER_COST} (you have ${d.balance}).`);
      else toast.error(typeof d === "string" ? d : "Couldn't generate a cover — please try again.");
    } finally { setGenerating(false); }
  };

  const apply = async () => {
    if (!preview) return;
    setApplying(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/cover/apply-generated`, { image: preview });
      toast.success("Cover set");
      onApplied?.({ cover_path: data.cover_path, updated_at: data.updated_at });
      reset();
      onOpenChange(false);
    } catch { toast.error("Couldn't save the cover — please try again."); }
    finally { setApplying(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!generating && !applying) { if (!v) reset(); onOpenChange(v); } }}>
      <DialogContent className="bg-popover max-w-2xl" data-testid="ai-cover-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-primary" /> Generate a cover with AI
          </DialogTitle>
          <DialogDescription>
            We’ll design a KDP-sized front cover from your title, genre and synopsis. Costs {COVER_COST} credits per image — regenerate as many times as you like.
          </DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-5 py-1">
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Art direction <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input value={style} onChange={(e) => setStyle(e.target.value)} data-testid="ai-cover-style"
                placeholder="e.g. moody watercolour, retro pulp, minimalist" />
            </div>
            <div className="space-y-1.5">
              <Label>Anything specific to include? <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={4} data-testid="ai-cover-brief"
                placeholder="e.g. a lone lighthouse at dusk, a raven, deep teal and gold palette" />
            </div>
            <Button onClick={generate} disabled={generating || applying} data-testid="ai-cover-generate" className="w-full gap-2">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : (preview ? <RefreshCw className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />)}
              {generating ? "Designing…" : (preview ? "Regenerate" : `Generate cover · ${COVER_COST} credits`)}
            </Button>
            <p className="text-[11px] text-muted-foreground">Tip: the more you describe the mood and imagery, the better the result. The title and your pen name are added automatically.</p>
          </div>

          <div className="flex flex-col">
            <Label className="mb-1.5">Preview</Label>
            <div className="flex-1 min-h-[18rem] rounded-xl border border-border bg-secondary/40 flex items-center justify-center overflow-hidden">
              {generating ? (
                <div className="flex flex-col items-center gap-2 text-muted-foreground text-sm">
                  <Loader2 className="w-6 h-6 animate-spin" /> Designing your cover…
                </div>
              ) : preview ? (
                <img src={preview} alt="Generated cover preview" data-testid="ai-cover-preview" className="max-h-[26rem] w-auto object-contain" />
              ) : (
                <span className="text-sm text-muted-foreground px-6 text-center">Your generated cover will appear here.</span>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => { reset(); onOpenChange(false); }} disabled={generating || applying}>Cancel</Button>
          <Button onClick={apply} disabled={!preview || generating || applying} data-testid="ai-cover-apply" className="gap-2">
            {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Use this cover
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
